/* eslint-disable @typescript-eslint/no-unused-vars  */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
import styled from "@emotion/styled";
import { ThemeKey } from "@smartsheet/design-tokens";
import { color } from "@smartsheet/design-tokens-react";
import { useForkRef } from "@smartsheet/lodestar-utils";
import { ChevronDownIcon } from "@smartsheet/ursa-icons";
import classNames from "classnames";
import React, { forwardRef, ReactElement, useState } from "react";
import { MenuItemProps, MenuProps, useMenu, UseMenuOptions } from "../menu";
import { getTabStyles } from "./Tab";
import { useTabsContext } from "./TabsContext";
import { TabMenuFlyoutStyled } from "./styled/TabMenuFlyoutStyled";
import { tabsTheme } from "./tabsTheme";

export interface TabMenuProps {
    /**
     * The children to render inside this TabMenu. Should be Tab components.
     */
    children: React.ReactNode;
    /**
     * Where the tab menu should be rendered
     * @default "work-surface"
     */
    renderOn: "work-surface" | "canvas";
    /**
     * Whether the tab is selected (always true for tab menu btn)
     */
    selected: boolean;
    /**
     * The theme mode to use for this component
     */
    themeMode: ThemeKey;
    /**
     * optional classname to be applied to the root element
     */
    className?: string;
    /**
     * optional props to be spread on the Menu and MenuItem components
     */
    overrides?: {
        MenuItemsProps?: MenuItemProps;
        TabMenuProps?: MenuProps;
    };
    /**
     * additional options for configuring the useMenu hook
     * */
    useMenuOptions?: UseMenuOptions;
}

export const TabMenuBase = forwardRef<HTMLDivElement, TabMenuProps>(
    ({ children, className, overrides, useMenuOptions, themeMode, renderOn, selected = true, ...rest }, ref) => {
        const { selectedId } = useTabsContext();
        const [isOpen, setIsOpen] = useState(false);

        const { getTargetHTMLProps, getMenuProps } = useMenu({
            targetRef: ref as React.Ref<HTMLButtonElement>,
            placement: "bottom",
            isOpen,
            onCloseRequested: () => setIsOpen(false),
            onOpenRequested: () => setIsOpen(true),
            ...useMenuOptions,
        });

        const removeCheckmark = (ref: HTMLButtonElement) => {
            //a checkmark is added to the selected tab, we need to remove it when it's the menu button
            if (ref) {
                const checkmark = ref.querySelector(".sds-tab-menu-item-checkmark");
                if (checkmark) {
                    checkmark.remove();
                }
            }
        };

        const addTabIndex = (ref: HTMLButtonElement) => {
            if (ref) {
                ref.tabIndex = 0;
            }
        };

        const menuBtnRef = useForkRef<HTMLButtonElement>(
            ref as React.Ref<HTMLButtonElement>,
            getTargetHTMLProps().ref,
            removeCheckmark,
            addTabIndex
        );

        return (
            <>
                <div className={classNames(className, `sds-tabs-menu`)} {...rest}>
                    {React.Children.map(children, (child) => {
                        if (!React.isValidElement(child)) {
                            return null;
                        }
                        //eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                        const { id }: { id: string } = child.props;
                        const isSelected = id === selectedId;
                        if (isSelected) {
                            return React.cloneElement(
                                child as ReactElement,
                                {
                                    ...getTargetHTMLProps(),
                                    className: classNames(child.props.className, "sds-tabs-menu-btn"),
                                    ref: menuBtnRef,
                                    tabIndex: 0, //this will be overriden by the parent component
                                    //so we override it again when the ref is set
                                    //leaving this in here to explain why we are mutating the ref
                                },
                                <div className="sds-tabs-menu-label" key={id}>
                                    {child.props.children}
                                    <ChevronDownIcon
                                        color={`var(--smar--com--color--text--primary--default, #184BC3)`}
                                        size={20}
                                    />
                                </div>
                            );
                        }
                    })}
                </div>
                <TabMenuFlyoutStyled {...getMenuProps()} layout="dense" {...overrides?.TabMenuProps}>
                    {children}
                </TabMenuFlyoutStyled>
            </>
        );
    }
);

TabMenuBase.displayName = "TabMenuBase";

const lodestarSpecificStyles = `
    border-bottom: none;
    [role="tab"] {
        &.sds-tabs-menu-btn {
            border-bottom: 2px solid ${tabsTheme.underlineSelectedColor("lodestar-light")};
        }
    }
`;

export const TabMenu = styled(TabMenuBase)`
    --sds--menu-item--gap--compact: 0;
    border-bottom: 3px solid ${color("color--border--primary--default", "#184bc3")};
    [role="tab"] {
        ${({ themeMode, renderOn, selected }) => getTabStyles(themeMode, renderOn, selected)}
        &.sds-tabs-menu-btn {
            display: block;
            &[aria-expanded="true"] {
                background: transparent;
                color: ${color("color--text--primary--default", "#184bc3")};
            }
            &:hover {
                background: transparent;
                color: ${color("color--text--primary--default", "#184bc3")};
                &:before {
                    display: none;
                }
            }
            :after {
                opacity: 0;
            }
        }
    }

    &:has(> [role="tab"]:focus) {
        border-bottom: none;
    }

    ${({ themeMode }) =>
        themeMode === "lodestar-light"
            ? lodestarSpecificStyles
            : `
        &:has(> [role="tab"]:hover) {
            border-bottom: 3px solid ${color("color--border--primary--default", "#184bc3")};
        }
    `}

    .sds-tabs-menu-label {
        display: flex;
        gap: 4px;
        align-items: center;
    }
`;
